<template>
  <li v-if="zone" class="zones-list--zone-item" :class="{ 'zones-list--active': active }">
    <div v-if="showCheck" class="zones-list--check">
      <Checkbox v-model="checkedZones" :value="zone" />
    </div>

    <div v-if="canRemove" class="zones-list--controls zone-list--controls-remove">
      <Button severity="contrast" aria-label="Remove zone" @click="onRemoveZone()">
        <IconRemove />
      </Button>
    </div>

    <div v-if="canEdit" class="zones-list--controls">
      <Button
        type="button"
        class="dots-button"
        @click="toggle"
        aria-haspopup="true"
        aria-controls="user_menu"
        outlined
      >
        <IconDots />
      </Button>

      <Menu ref="zoneMenu" id="zone_menu" :model="items" :popup="true">
        <template #item="{ item }">
          <Button v-if="item.label === 'Edit'" @click="onEditZone" outlined class="p-button-list">
            <IconEdit />
            Edit
          </Button>
          <Button
            v-if="item.label === 'Delete'"
            @click="onRemoveZone"
            outlined
            class="p-button-list"
          >
            <IconDelete />
            Delete
          </Button>
        </template>
      </Menu>
    </div>

    <div class="zones-list--title">{{ zone.name }}</div>
    <div class="zones-list--count">
      <IconSensorRow />
      {{ zone.sensors.length }} Sensor<template v-if="zone.sensors.length !== 1">s</template>
    </div>
    <div v-if="showTypes" class="chips">
      <Chip v-for="type in zone.categories" :key="type">{{ displayZoneName(type) }}</Chip>
    </div>
  </li>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import type { EventListItem, ZoneType } from '@/types/Event';

import { Button, Checkbox, Menu } from 'primevue';

import Chip from '@/components/global/Chip.vue';

import IconDots from '@/icons/menu/IconDots.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconDelete from '@/icons/IconDelete.svg?component';
import IconRemove from '@/icons/IconRemove.svg?component';
import IconSensorRow from '@/icons/IconSensorRow.svg?component';

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

const zone = defineModel<ZoneType>('zone');
const checkedZones = defineModel<ZoneType[]>('checkedZones');

const props = withDefaults(
  defineProps<{
    active?: boolean;
    index?: number;
    canEdit?: boolean;
    canRemove?: boolean;
    showTypes?: boolean;
    showCheck?: boolean;
  }>(),
  {
    active: false,
    canEdit: false,
    canRemove: false,
    showTypes: false,
    showCheck: true,
  },
);

const zoneMenu = ref();

const items = ref([{ label: 'Edit' }, { label: 'Delete' }]);

const toggle = (event: MouseEvent) => {
  zoneMenu.value.toggle(event);
};

const emit = defineEmits(['onRemoveZone', 'onEditZone']);

const displayZoneName = (zoneId: number) => {
  return eventsStore.eventList?.zone_category.find((zone: EventListItem) => zone.id === zoneId)
    ?.name;
};

const onEditZone = () => {
  emit('onEditZone', zone.value, props.index);
};

const onRemoveZone = () => {
  emit('onRemoveZone');
};
</script>

<style lang="scss">
.zones-list--zone-item {
  @include mixins.flex-center($horiz: flex-start, $wrap: true);
  @include sizing.padding('xs');
  @include mixins.rounded-border();
  background: variables.$vantage-base-primitives-color-border-100;
  flex-basis: calc(33% - 4px);
  flex-shrink: 0;
  min-height: 100px;
  position: relative;

  &.zones-list--active {
    border-color: variables.$vantage-base-primitives-color-primary-500;
  }
}

.modal-body-add-zone .zones-list--zone-item .chips {
  border: 0;
  flex-basis: 100%;
  justify-content: flex-start;
  padding: 0;
}

.zones-list--check {
  @include mixins.flex-center($horiz: flex-start);
  margin-right: auto;
}

.zones-list--title {
  @include typography.font-body($size: md, $weight: medium);
  color: variables.$vantage-base-primitives-color-primary-500;
  flex-basis: 100%;
  margin-top: auto;
}

.zones-list--count {
  @include mixins.flex-center($horiz: flex-start);
  @include typography.font-body($size: sm, $weight: normal);
  color: variables.$vantage-base-primitives-color-tertiary-400;

  svg {
    color: variables.$vantage-base-primitives-color-tertiary-400;
    margin-right: variables.$vantage-base-sizing-xxxs;
  }
}

.zone-list--controls-remove {
  position: absolute;
  top: variables.$vantage-base-sizing-xxs;
  right: 0;

  svg {
    margin-right: 0;
  }

  .p-button {
    border: 0;
  }

  .p-button.p-button-contrast:not(:disabled):hover {
    background: transparent;
    border: 0;
  }
}
</style>
